import { Component, EventEmitter, Input, Output, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { EnumService } from '@ops/shared-types';

export interface CustomCurrencyModalConfig {
  // Context-specific validation
  excludeCurrencyCodes?: string[];
  excludeReason?: string;
  
  // Success callback data
  onSuccess?: {
    updateField?: string; // Field to update on success
    additionalData?: any; // Additional data to pass back
  };
}

@Component({
  selector: 'ops-custom-currency-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule
  ],
  templateUrl: './ops-custom-currency-modal.component.html',
  styleUrl: './ops-custom-currency-modal.component.scss'
})
export class OpsCustomCurrencyModalComponent implements OnInit, OnDestroy {
  @Input() isVisible: boolean = false;
  @Input() config: CustomCurrencyModalConfig = {};
  @Output() currencyAdded = new EventEmitter<{ 
    currencyCode: string; 
    updateField?: string; 
    additionalData?: any; 
  }>();
  @Output() modalClosed = new EventEmitter<void>();

  newCurrency = {
    shortName: ''
  };
  validationError: string = '';

  constructor(private enumService: EnumService) {}

  ngOnInit(): void {
    if (this.isVisible) {
      this.resetForm();
    }
  }

  ngOnDestroy(): void {
    this.resetForm();
  }

  resetForm(): void {
    this.newCurrency = {
      shortName: ''
    };
    this.validationError = '';
  }

  onCurrencyCodeChange(): void {
    // Auto-uppercase and limit to 3 characters
    this.newCurrency.shortName = this.newCurrency.shortName.toUpperCase().slice(0, 3);
    this.validateCurrency();
  }

  validateCurrency(): void {
    this.validationError = '';
    
    if (!this.newCurrency.shortName.trim()) {
      this.validationError = 'Currency code is required';
      return;
    }

    const validation = this.enumService.validateCustomCurrency(this.newCurrency.shortName);
    if (!validation.isValid) {
      this.validationError = validation.error || 'Invalid currency code';
      return;
    }

    // Check against excluded currency codes
    if (this.config.excludeCurrencyCodes?.includes(this.newCurrency.shortName)) {
      this.validationError = this.config.excludeReason || 'This currency is already in use';
      return;
    }
  }

  addCurrency(): void {
    this.validateCurrency();
    
    if (this.validationError) {
      return;
    }

    const result = this.enumService.addCustomCurrency(
      this.newCurrency.shortName, // Use shortName as name
      this.newCurrency.shortName,
      this.newCurrency.shortName // Use shortName as symbol
    );

    if (result.success) {
      this.currencyAdded.emit({
        currencyCode: this.newCurrency.shortName,
        updateField: this.config.onSuccess?.updateField,
        additionalData: this.config.onSuccess?.additionalData
      });
      this.closeModal();
    } else {
      this.validationError = result.error || 'Failed to add currency';
    }
  }

  closeModal(): void {
    this.isVisible = false;
    this.resetForm();
    this.modalClosed.emit();
  }

  onBackdropClick(): void {
    this.closeModal();
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.closeModal();
    }
  }
}
